import * as React from 'react'
import { useNavigate, type NavigateFunction, type NavigateOptions, type To } from 'react-router-dom'

interface UseUnsavedChangesBlockerOptions {
    when: boolean
    /**
     * Called when a navigation is intercepted while `when` is true.
     * Return `false` to release the navigation (e.g. the user confirmed
     * losing their changes); any other return keeps it blocked.
     */
    onBlock: () => boolean | void
}

type GuardResult = 'idle' | 'block' | 'release'
// `silent` guards (beforeunload) must not open UI — the browser shows its
// own native confirmation, so an active guard always blocks there.
type Guard = (opts: { silent: boolean }) => GuardResult

const SENTINEL_KEY = '__unsaved_changes_blocker__'

const guards = new Set<Guard>()

// True while we replay the back navigation the user just confirmed.
let releasingBack = false

const runGuards = (opts: { silent: boolean } = { silent: false }): GuardResult => {
    let result: GuardResult = 'idle'
    for (const guard of guards) {
        const r = guard(opts)
        if (r === 'block') result = 'block'
        else if (r === 'release' && result === 'idle') result = 'release'
    }
    return result
}

let domInstalled = false
const installDomListeners = () => {
    if (domInstalled || typeof window === 'undefined') return
    domInstalled = true

    const handleClick = (e: MouseEvent) => {
        if (e.defaultPrevented) return
        if (e.button !== 0) return
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

        const target = e.target as Element | null
        if (!target || typeof target.closest !== 'function') return
        const link = target.closest('a') as HTMLAnchorElement | null
        if (!link) return
        if (link.target && link.target !== '' && link.target !== '_self') return
        if (link.hasAttribute('download')) return

        const href = link.getAttribute('href')
        if (!href) return
        if (href.startsWith('mailto:') || href.startsWith('tel:')) return

        const linkUrl = new URL(link.href, window.location.href)
        if (linkUrl.origin !== window.location.origin) return
        if (
            linkUrl.pathname === window.location.pathname &&
            linkUrl.search === window.location.search
        ) {
            return
        }

        if (runGuards() === 'block') {
            e.preventDefault()
            e.stopPropagation()
        }
    }

    const handlePopstate = () => {
        // Skip the popstate produced by our own history.back() below,
        // otherwise the guard would prompt a second time.
        if (releasingBack) {
            releasingBack = false
            return
        }
        const result = runGuards()
        if (result === 'block') {
            window.history.pushState({ [SENTINEL_KEY]: true }, '', window.location.href)
        } else if (result === 'release') {
            // The pop only consumed our sentinel entry (same URL). The user
            // confirmed leaving — continue back to the real previous entry.
            releasingBack = true
            window.history.back()
        }
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (runGuards({ silent: true }) === 'block') {
            e.preventDefault()
            e.returnValue = ''
        }
    }

    document.addEventListener('click', handleClick, { capture: true })
    window.addEventListener('popstate', handlePopstate)
    window.addEventListener('beforeunload', handleBeforeUnload)
}

export const useUnsavedChangesBlocker = ({ when, onBlock }: UseUnsavedChangesBlockerOptions) => {
    const whenRef = React.useRef(when)
    const onBlockRef = React.useRef(onBlock)

    React.useEffect(() => {
        whenRef.current = when
        onBlockRef.current = onBlock
    })

    React.useEffect(() => {
        installDomListeners()

        const guard: Guard = ({ silent }) => {
            if (!whenRef.current) return 'idle'
            if (silent) return 'block'
            return onBlockRef.current() === false ? 'release' : 'block'
        }
        guards.add(guard)
        return () => {
            guards.delete(guard)
        }
    }, [])

    // Arm a sentinel entry (same URL) only while there is work to protect,
    // so the first back press lands on our popstate instead of leaving.
    // A clean page must not cost the user an extra back press.
    React.useEffect(() => {
        if (!when) return
        const state = window.history.state as Record<string, unknown> | null
        if (!state?.[SENTINEL_KEY]) {
            window.history.pushState({ [SENTINEL_KEY]: true }, '', window.location.href)
        }
    }, [when])
}

export const useGuardedNavigate = (): NavigateFunction => {
    const navigate = useNavigate()
    return React.useMemo<NavigateFunction>(() => {
        const guarded = (to: To | number, options?: NavigateOptions) => {
            if (runGuards() === 'block') return
            if (typeof to === 'number') return navigate(to)
            return navigate(to, options)
        }
        return guarded as NavigateFunction
    }, [navigate])
}
